import { tva } from '@gluestack-ui/utils/nativewind-utils'

import { TextTone } from '../text/variants'

export type TimelineBarColor =
    | 'info'
    | 'critical'
    | 'success'
    | 'caution'
    | 'warning'
    | 'neutral'
    | 'silver'

export type TimelineBarBorderStyle = 'solid' | 'dashed'

/**
 * Fills add `canvas` on top of the border-capable colours: an outlined bar sits on the page
 * canvas rather than the default elevation (Figma `AOS/NOTAM`, `AOS/Stands&GatesTimeline`).
 */
export type TimelineBarBackground = TimelineBarColor | 'canvas'

const timelineBar = tva({
    variants: {
        background: {
            none: '',
            info: 'bg-status-info hover:bg-status-info-hover active:bg-status-info-pressed',
            critical:
                'bg-status-critical hover:bg-status-critical-hover active:bg-status-critical-pressed',
            success:
                'bg-status-success hover:bg-status-success-hover active:bg-status-success-pressed',
            caution:
                'bg-status-caution hover:bg-status-caution-hover active:bg-status-caution-pressed',
            warning:
                'bg-status-warning hover:bg-status-warning-hover active:bg-status-warning-pressed',
            neutral:
                'bg-status-neutral hover:bg-status-neutral-hover active:bg-status-neutral-pressed',
            silver: 'bg-graphics-silver hover:bg-graphics-silver-hover active:bg-graphics-silver-pressed',
            canvas: 'bg-aos-bg-canvas hover:bg-aos-bg-canvas-hover',
        },
        borderColor: {
            none: '',
            info: 'border-status-info hover:border-status-info-hover active:border-status-info-pressed',
            critical:
                'border-status-critical hover:border-status-critical-hover active:border-status-critical-pressed',
            success:
                'border-status-success hover:border-status-success-hover active:border-status-success-pressed',
            caution:
                'border-status-caution hover:border-status-caution-hover active:border-status-caution-pressed',
            warning:
                'border-status-warning hover:border-status-warning-hover active:border-status-warning-pressed',
            neutral:
                'border-status-neutral hover:border-status-neutral-hover active:border-status-neutral-pressed',
            silver: 'border-graphics-silver hover:border-graphics-silver-hover active:border-graphics-silver-pressed',
        },
        borderStyle: { solid: '', dashed: '' },
        hasBorder: { true: 'border-[3px]', false: '' },
    },
    compoundVariants: [
        // Default fill when no background is set — depends on the border style.
        {
            background: 'none',
            borderStyle: 'solid',
            class: 'bg-aos-bg-elevation hover:bg-aos-bg-elevation-hover active:bg-aos-bg-elevation-pressed',
        },
        {
            background: 'none',
            borderStyle: 'dashed',
            class: 'bg-aos-bg-canvas hover:bg-aos-bg-canvas-hover',
        },
        // Border line style only applies when a border is actually drawn.
        { hasBorder: true, borderStyle: 'solid', class: 'border-solid' },
        { hasBorder: true, borderStyle: 'dashed', class: 'border-dashed' },
    ],
    defaultVariants: {
        background: 'none',
        borderColor: 'none',
        borderStyle: 'solid',
        hasBorder: false,
    },
})

export type TimelineBarVisual = {
    background?: TimelineBarBackground
    border?: TimelineBarColor
    borderStyle?: TimelineBarBorderStyle
}

export type ResolvedTimelineBar = {
    container: string
    textTone: TimelineBarTone
}

export type TimelineBarTone = Extract<TextTone, 'default' | 'on-graphics' | 'knockout'>

const inkForFill = (background: TimelineBarBackground | undefined): TimelineBarTone => {
    if (!background || background === 'canvas') {
        return 'default'
    }
    return background === 'critical' ? 'knockout' : 'on-graphics'
}

export const resolveTimelineBar = ({
    background,
    border,
    borderStyle = 'solid',
}: TimelineBarVisual): ResolvedTimelineBar => {
    const showBorder = !!border && border !== background
    return {
        container: timelineBar({
            background: background ?? 'none',
            borderColor: showBorder && border ? border : 'none',
            borderStyle,
            hasBorder: showBorder,
        }),
        textTone: inkForFill(background),
    }
}
